import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Icon,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  useToast,
  useDisclosure,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Textarea,
  Link,
  Checkbox,
} from '@chakra-ui/react';
import {
    FaGift,
    FaCheckCircle,
    FaClock,
    FaHandshake,
    FaInfoCircle,
    FaArrowLeft,
    FaCloud,  
    FaSignOutAlt,
    FaCog,
    FaComments,
    FaPaperPlane,
    // FaRobot, // Copilot temporarily hidden
    // FaCoins,
    // FaChartBar,
  } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
// import copilotApiService from '../services/copilotApi';
import Footer from './Footer';
import NavDropdowns from './NavDropdowns';

const BuyCredits = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Free tier eligibility state
  const [freeTierEligibility, setFreeTierEligibility] = useState(null);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(true);

  const { isOpen: isContactOpen, onOpen: onContactOpen, onClose: onContactClose } = useDisclosure();
  const { isOpen: isCancelOpen, onOpen: onCancelOpen, onClose: onCancelClose } = useDisclosure();  
  const [contactError, setContactError] = useState(null);


  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(null);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [couponError, setCouponError] = useState(null);
  const [couponSuccess, setCouponSuccess] = useState(null);

  // Add these with the other state declarations (around line 67-68)
  const { isOpen: isPurchaseConfirmOpen, onOpen: onPurchaseConfirmOpen, onClose: onPurchaseConfirmClose } = useDisclosure();
  const [selectedPlan, setSelectedPlan] = useState(null); // { tier: 'tenhours' | 'twentyhours', hours: 10 | 20, price: 150 | 300 }
  const [purchaseConsent, setPurchaseConsent] = useState({
    immediateProvision: false,
    termsAccepted: false,
  });

  // Contact form state (for Enterprise)
  const [contactForm, setContactForm] = useState({
    subject: '',
    message: '',
    user_name: '',
    user_email: ''
  });
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);

  // Copilot credits state — temporarily hidden
  /*
  const [copilotBalance, setCopilotBalance] = useState(null);
  const [copilotBalanceLoading, setCopilotBalanceLoading] = useState(true);
  const [isBuyingCopilotCredits, setIsBuyingCopilotCredits] = useState(false);
  */

  // Color mode values
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    fetchUserDetails();
    fetchSubscriptionDetails();
    checkFreeTierEligibility();
    // fetchCopilotBalance();
  }, []);

  /*
  const fetchCopilotBalance = async () => {
    try {
      setCopilotBalanceLoading(true);
      const data = await copilotApiService.getBalance();
      setCopilotBalance(data);
    } catch {
      setCopilotBalance(null);
    } finally {
      setCopilotBalanceLoading(false);
    }
  };

  const handleBuyCopilotCredits = async () => {
    try {
      setIsBuyingCopilotCredits(true);
      const data = await copilotApiService.buyCredits();
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) {
      toast({ title: 'Error', description: err.message || 'Failed to start checkout', status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsBuyingCopilotCredits(false);
    }
  };
  */

  const handleLogout = () => {
    logout();
    navigate('/');
  };


  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const details = await apiService.getUserDetails();
      setUserDetails(details);
            
    } catch (err) {
      console.error('Failed to fetch user details:', err);
      setError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const checkFreeTierEligibility = async () => {
    try {
      setIsCheckingEligibility(true);
      const eligibility = await apiService.checkFreeTierEligibility();
      setFreeTierEligibility(eligibility);
    } catch (err) {
      console.error('Failed to check free tier eligibility:', err);
      // If check fails, assume not eligible for safety
      setFreeTierEligibility({ 
        is_eligible: false, 
        reason: 'Unable to verify eligibility. Please try again later.' 
      });
    } finally {
      setIsCheckingEligibility(false);
    }
  };

  const fetchSubscriptionDetails = async () => {
    try {
      setSubscriptionLoading(true);
      setSubscriptionError(null);
      const details = await apiService.getSubscriptionDetails();
      setSubscriptionDetails(details);
    } catch (err) {
      console.error('Failed to fetch subscription details:', err);
      setSubscriptionError('Failed to load subscription details');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setIsValidatingCoupon(true);
    setCouponError(null);
    setCouponSuccess(null);

    try {
      // First validate the promotion code
      const validationResponse = await apiService.validatePromotionCode(couponCode);
      
      // If validation is successful, proceed to checkout with the promotion code
      const checkoutData = {
        promotion_code: couponCode,
        subscription_tier: 'tenhours'
      };

      const checkoutResponse = await apiService.createSubscriptionCheckout(checkoutData);
      
      if (checkoutResponse.checkout_url) {
        setCouponSuccess('Promotion code applied! Redirecting to checkout...');
        setTimeout(() => {
          window.location.href = checkoutResponse.checkout_url;
        }, 1500);
      }
    } catch (err) {
      setCouponError(err.response?.data?.error || 'Invalid or expired promotion code');
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleCouponInputChange = (e) => {
    setCouponCode(e.target.value);
    if (couponError) setCouponError(null);
    if (couponSuccess) setCouponSuccess(null);
  };


  const handleFreeUsage = async () => {
    try {
      setIsSubmittingContact(true);
      
      const checkoutData = {
        subscription_tier: 'free'
      };
      
      const response = await apiService.createSubscriptionCheckout(checkoutData);
      
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        toast({
          title: 'Error',
          description: 'Failed to create checkout session. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Failed to upgrade:', err);
      toast({
        title: 'Error',
        description: err.response?.data?.error || 'Failed to initiate upgrade. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingContact(false);
    }
  };


  const handleUpgrade10Hours = async () => {
    try {
      setIsSubmittingContact(true);
      
      const checkoutData = {
        subscription_tier: 'tenhours'
      };
      
      const response = await apiService.createSubscriptionCheckout(checkoutData);
      
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        toast({
          title: 'Error',
          description: 'Failed to create checkout session. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Failed to upgrade:', err);
      toast({
        title: 'Error',
        description: err.response?.data?.error || 'Failed to initiate upgrade. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const handleUpgrade20Hours = async () => {
    try {
      setIsSubmittingContact(true);
      
      const checkoutData = {
        subscription_tier: 'twentyhours'
      };
      
      const response = await apiService.createSubscriptionCheckout(checkoutData);
      
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        toast({
          title: 'Error',
          description: 'Failed to create checkout session. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Failed to upgrade:', err);
      toast({
        title: 'Error',
        description: err.response?.data?.error || 'Failed to initiate upgrade. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingContact(false);
    }
  };


  const openPurchaseConfirmModal = (tier, hours, price) => {
    setSelectedPlan({ tier, hours, price });
    setPurchaseConsent({ immediateProvision: false, termsAccepted: false });
    onPurchaseConfirmOpen();
  };
  const handleConfirmedPurchase = async () => {
    if (!selectedPlan) return;
    
    try {
      setIsSubmittingContact(true);
      
      // Build the acknowledged text
      const acknowledgedText = `I expressly request the immediate provision of the digital service and acknowledge that I lose my right of withdrawal once the service has been used. I have read and accept the Terms of Service and the Privacy Policy.`;
      
      // Call waiver acknowledgment API first
      await apiService.acknowledgeWaiver(selectedPlan.tier, acknowledgedText);
      
      // Then proceed to Stripe checkout
      const checkoutData = {
        subscription_tier: selectedPlan.tier
      };
      
      const response = await apiService.createSubscriptionCheckout(checkoutData);
      
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        toast({
          title: 'Error',
          description: 'Failed to create checkout session. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Failed to complete purchase:', err);
      toast({
        title: 'Error',
        description: err.message || 'Failed to initiate purchase. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingContact(false);
      onPurchaseConfirmClose();
    }
  };

  const handleContactInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  


  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.subject.trim() || !contactForm.message.trim()) {
      setContactError('Subject and message are required');
      return;
    }
  
    setIsSubmittingContact(true);
    setContactError(null);
  
    try {
      await apiService.sendContactMessage(contactForm);
      
      toast({
        title: 'Message Sent Successfully',
        description: 'Thank you for contacting us. We will get back to you soon!',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
  
      // Reset form and close modal
      setContactForm({
        subject: '',
        message: '',
        user_name: '',
        user_email: ''
      });
      onContactClose();
    } catch (err) {
      console.error('Failed to send contact message:', err);
      setContactError(err.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmittingContact(false);
    }
  };
  

  const openContactSalesModal = () => {
    // Pre-fill user details and set subject for sales inquiry
    if (userDetails) {
      setContactForm(prev => ({
        ...prev,
        user_name: `${userDetails.first_name} ${userDetails.last_name}`,
        user_email: userDetails.email,
        subject: 'Enterprise Plan Inquiry',
        message: 'Hello, I am interested in learning more about the Enterprise plan and would like to discuss pricing and features. Please contact me to schedule a consultation.\n\nThank you!'
      }));
    }
    onContactOpen();  // Make sure this is here
  };

  if (subscriptionLoading) {
    return (
      <Box minH="100vh" bg={bg}>
        <Container maxW="7xl" py={8}>
          <VStack spacing={8}>
            <Spinner size="xl" color="brand.500" />
            <Text color="gray.600">Loading subscription plans...</Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={bg}>
    {/* Header */}
    <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200">
      <Container maxW="7xl" py={4}>
        <HStack justify="space-between">
          <HStack 
            spacing={1} 
            cursor="pointer" 
            onClick={() => navigate('/dashboard')} 
            _hover={{ opacity: 0.8 }} 
            transition="opacity 0.2s"
            borderRadius="md"
            p={2}
            _active={{ transform: "scale(0.98)" }}
          >
            <Image
              src="/simple-logo.png"
              alt="SRS Logo"
              boxSize={14}
              objectFit="contain"
            />
            <Box>
              <Heading size="lg" color="brand.500">SRS</Heading>
              <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
            </Box>
          </HStack>
          <HStack spacing={4}>
            <NavDropdowns />
            <Button
              leftIcon={<FaCloud />}
              colorScheme="brand"
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
            >
              Dashboard
            </Button>
            <Button
              leftIcon={<FaSignOutAlt />}
              variant="ghost"
              size="sm"
              onClick={handleLogout}
            >
              Sign Out
            </Button>
          </HStack>
        </HStack>
      </Container>
    </Box>

    {/* Page Title Section 
    <Box bg="gray.50" borderBottom="1px" borderColor="gray.200" py={6}>
    <Container maxW="7xl">
        <VStack spacing={2} align="start">
        <Heading size="lg" color="brand.600">Buy More Credits</Heading>
        <Text color="gray.600">Choose a plan or apply a coupon code</Text>
        </VStack>
    </Container>
    </Box>*/}

      <Container maxW="7xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* Apply Coupon Code Section */}
          <Card bg={cardBg} shadow="sm">
            <CardHeader>
              <HStack spacing={3}>
                <Icon as={FaGift} color="brand.500" boxSize={6} />
                <Box>
                  <Heading size="md">Apply Coupon Code</Heading>
                  <Text fontSize="sm" color="gray.600">
                    Have a promotion code? Apply it here to get discounts on your subscription
                  </Text>
                </Box>
              </HStack>
            </CardHeader>
            <CardBody pt={0}>
              <VStack spacing={4} align="stretch">
                <HStack spacing={3}>
                  <FormControl flex="1">
                    <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
                      Promotion Code
                    </FormLabel>
                    <Input
                      value={couponCode}
                      onChange={handleCouponInputChange}
                      placeholder="Enter your coupon code"
                      size="md"
                      textTransform="uppercase"
                      borderColor="gray.300"
                      _hover={{ borderColor: "gray.400" }}
                      _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
                      
                    />
                  </FormControl>
                  <Button
                    colorScheme="brand"
                    onClick={handleValidateCoupon}
                    isLoading={isValidatingCoupon}
                    loadingText="Processing..."
                    isDisabled={!couponCode.trim()}
                    size="md"
                    mt={6}
                  >
                    Apply & Checkout
                  </Button>
                </HStack>

                {couponError && (
                  <Alert status="error" borderRadius="md" size="sm">
                    <AlertIcon />
                    {couponError}
                  </Alert>
                )}

                {couponSuccess && (
                  <Alert status="success" borderRadius="md" size="sm">
                    <AlertIcon />
                    {couponSuccess}
                  </Alert>
                )}

                <Box bg="blue.50" p={3} borderRadius="md" border="1px solid" borderColor="blue.200">
                  <VStack spacing={2} align="start">
                    <HStack spacing={2}>
                      <Icon as={FaInfoCircle} color="blue.500" boxSize={4} />
                      <Text fontSize="sm" fontWeight="semibold" color="blue.700">
                        About Promotion Codes
                      </Text>
                    </HStack>
                    <VStack spacing={1} align="start" pl={2}>
                      <Text fontSize="sm" color="blue.600">
                        - Valid codes will redirect you to checkout with discount applied
                      </Text>
                      <Text fontSize="sm" color="blue.600">
                        - Enter the code exactly as provided (case-insensitive)
                      </Text>
                      <Text fontSize="sm" color="blue.600">
                        - Some codes may have expiration dates or usage limits
                      </Text>
                    </VStack>
                  </VStack>
                </Box>
              </VStack>
            </CardBody>
          </Card>

          {/* Available Subscription Plans */}
          <Card bg={cardBg} shadow="sm">
            <CardHeader>
              <Heading size="md">Usage-Based Plans</Heading>
              <Text fontSize="sm" color="gray.600">Choose the plan that best fits your research needs. All plans are one-time purchases.</Text>
            </CardHeader>
            <CardBody pt={0}>
              <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
                {/* €1 Starter Plan */}
                <Box 
                  border="2px" 
                  borderColor="gray.200" 
                  rounded="lg" 
                  p={6} 
                  textAlign="center"
                  bg="gray.50"
                  _hover={{ borderColor: "gray.300", shadow: "md" }}
                  transition="all 0.2s"
                  display="flex"
                  flexDirection="column"
                  minH="400px"
                >
                  <VStack spacing={4} flex="1">
                    <VStack spacing={2}>
                      <Icon as={FaGift} color="gray.500" boxSize={8} />
                      <Heading size="md" color="gray.700">€1 Starter</Heading>
                      
                    </VStack>
                    
                    <VStack spacing={2} align="start" w="full" flex="1">
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                        <Text fontSize="sm">Unlimited storage</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                        <Text fontSize="sm">2 hours compute time</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                        <Text fontSize="sm">Basic GPU access</Text>
                      </HStack>
                    </VStack>
                    
                    {/* Show eligibility message when not eligible */}
                    {!isCheckingEligibility && !freeTierEligibility?.is_eligible && freeTierEligibility?.reason && (
                      <Alert status="warning" size="sm" fontSize="xs" borderRadius="md">
                        <AlertIcon boxSize={3} />
                        <Text fontSize="xs">{freeTierEligibility.reason}</Text>
                      </Alert>
                    )}
                    
                    <Button 
                      colorScheme="gray" 
                      variant="outline" 
                      size="sm" 
                      w="full"
                      onClick={handleFreeUsage}
                      isDisabled={!freeTierEligibility?.is_eligible || isCheckingEligibility}
                      isLoading={isCheckingEligibility}
                      loadingText="Checking..."
                      mt="auto"
                    >
                      {isCheckingEligibility 
                        ? 'Checking Eligibility...'
                        : freeTierEligibility?.is_eligible
                          ? 'Start with €1'
                          : 'Not Eligible'
                      }
                    </Button>
                  </VStack>
                </Box>

                {/* 10 Hours Plan */}
                <Box 
                  border="2px" 
                  borderColor="brand.500" 
                  rounded="lg" 
                  p={6} 
                  textAlign="center"
                  bg="brand.50"
                  _hover={{ borderColor: "brand.500", shadow: "md" }}
                  transition="all 0.2s"
                  display="flex"
                  flexDirection="column"
                  minH="400px"
                >
                  <VStack spacing={4} flex="1">
                    <VStack spacing={2}>
                      <Icon as={FaClock} color="brand.500" boxSize={8} />
                      <Heading size="md" color="brand.600">10 Hours</Heading>
                      <Text fontSize="2xl" fontWeight="bold" color="brand.600">€150</Text>
                      <Text fontSize="sm" color="gray.500">excl. VAT</Text>
                      <Text fontSize="sm" color="gray.500">one-time purchase</Text>
                    </VStack>
                    
                    <VStack spacing={2} align="start" w="full" flex="1">
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="brand.500" boxSize={4} />
                        <Text fontSize="sm">Priority queue</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="brand.500" boxSize={4} />
                        <Text fontSize="sm">Unlimited storage</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="brand.500" boxSize={4} />
                        <Text fontSize="sm">10 hours compute time</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="brand.500" boxSize={4} />
                        <Text fontSize="sm">High-performance GPUs</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="brand.500" boxSize={4} />
                        <Text fontSize="sm">Email support</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="brand.500" boxSize={4} />
                        <Text fontSize="sm">AI Integration</Text>
                      </HStack>
                    </VStack>
                    
                    <Button 
                      colorScheme="brand"
                      variant="solid"
                      size="sm" 
                      w="full"
                      mt="auto"
                      onClick={() => openPurchaseConfirmModal('tenhours', 10, 150)}
                      isLoading={isSubmittingContact}
                      loadingText="Redirecting..."
                      

                    >
                      Buy 10 Hours
                    </Button>
                  </VStack>
                </Box>

                {/* 20 Hours Plan */}
                <Box 
                  border="2px" 
                  borderColor="green.300" 
                  rounded="lg" 
                  p={6} 
                  textAlign="center"
                  bg="green.50"
                  _hover={{ borderColor: "green.400", shadow: "md" }}
                  transition="all 0.2s"
                  position="relative"
                  display="flex"
                  flexDirection="column"
                  minH="400px"
                >
                  <Badge 
                    colorScheme="green" 
                    position="absolute" 
                    top="-10px" 
                    left="50%" 
                    transform="translateX(-50%)"
                    px={3}
                    py={1}
                  >
                    SAVE €20
                  </Badge>
                  <VStack spacing={4} flex="1">
                    <VStack spacing={2}>
                      <Icon as={FaClock} color="green.500" boxSize={8} />
                      <Heading size="md" color="green.600">22 Hours</Heading>
                      <Text fontSize="2xl" fontWeight="bold" color="green.600">€300</Text>
                      <Text fontSize="sm" color="gray.500">excl. VAT</Text>
                      <Text fontSize="sm" color="gray.500">one-time purchase</Text>
                    </VStack>
                                            
                    <VStack spacing={2} align="start" w="full" flex="1">
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                        <Text fontSize="sm">Priority queue</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                        <Text fontSize="sm">Unlimited storage</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                        <Text fontSize="sm">22 hours compute time</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                        <Text fontSize="sm">High-performance GPUs</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                        <Text fontSize="sm">Email support</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                        <Text fontSize="sm">AI Integration</Text>
                      </HStack>
                    </VStack>
                                            
                    <Button 
                      colorScheme="green"
                      variant="solid"
                      size="sm" 
                      w="full"
                      mt="auto"
                      onClick={() => openPurchaseConfirmModal('twentyhours', 22, 300)}
                      isLoading={isSubmittingContact}
                      loadingText="Redirecting..."
                      

                    >
                      Buy 22 Hours
                    </Button>
                  </VStack>
                </Box>

                {/* Enterprise Plan */}
                <Box 
                  border="2px" 
                  borderColor="yellow.300" 
                  rounded="lg" 
                  p={6} 
                  textAlign="center"
                  bg="yellow.50"
                  _hover={{ borderColor: "yellow.400", shadow: "md" }}
                  transition="all 0.2s"
                  display="flex"
                  flexDirection="column"
                  minH="400px"
                >
                  <VStack spacing={4} flex="1">
                    <VStack spacing={2}>
                      <Icon as={FaHandshake} color="yellow.500" boxSize={8} />
                      <Heading size="md" color="yellow.600">Enterprise</Heading>
                      <Text fontSize="2xl" fontWeight="bold" color="yellow.600">Custom</Text>
                      <Text fontSize="sm" color="gray.500">custom pricing</Text>
                    </VStack>
                    
                    <VStack spacing={2} align="start" w="full" flex="1" textAlign="left">
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="yellow.500" boxSize={4} />
                        <Text fontSize="sm">Unlimited storage</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="yellow.500" boxSize={4} />
                        <Text fontSize="sm">Unlimited compute time</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="yellow.500" boxSize={4} />
                        <Text fontSize="sm">Unlimited concurrent simulations</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="yellow.500" boxSize={4} />
                        <Text fontSize="sm">Premium GPU clusters</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="yellow.500" boxSize={4} />
                        <Text fontSize="sm">Dedicated resources</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="yellow.500" boxSize={4} />
                        <Text fontSize="sm">Priority support</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="yellow.500" boxSize={4} />
                        <Text fontSize="sm">Custom integrations</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="yellow.500" boxSize={4} />
                        <Text fontSize="sm">Advanced AI Integration</Text>
                      </HStack>
                    </VStack>
                    
                    <Button 
                      colorScheme="yellow" 
                      size="sm" 
                      w="full"
                      isDisabled={subscriptionDetails?.subscription_tier === 'enterprise'}
                      mt="auto"
                      onClick={subscriptionDetails?.subscription_tier === 'enterprise' ? undefined : openContactSalesModal}
                    >
                      {subscriptionDetails?.subscription_tier === 'enterprise' ? 'Current Plan' : 'Contact Sales'}
                    </Button>
                  </VStack>
                </Box>
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Copilot credits section temporarily hidden */}
        </VStack>
      </Container>

        {/* Contact Us Modal */}
        <Modal isOpen={isContactOpen} onClose={onContactClose} size="lg">
        <ModalOverlay />
        <ModalContent>
            <ModalHeader>
            <HStack spacing={3}>
                <Icon as={FaComments} color="brand.500" boxSize={6} />
                <Text>Contact Us</Text>
            </HStack>
            </ModalHeader>
            <ModalCloseButton />
            <form onSubmit={handleContactSubmit}>
            <ModalBody>
                {contactError && (
                <Alert status="error" borderRadius="md" mb={4}>
                    <AlertIcon />
                    {contactError}
                </Alert>
                )}
                
                <VStack spacing={4}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                    <FormControl>
                    <FormLabel fontSize="sm" fontWeight="semibold">Name</FormLabel>
                    <Input
                        name="user_name"
                        value={contactForm.user_name}
                        placeholder="Your full name"
                        size="sm"
                        isReadOnly
                        bg="gray.50"
                        _focus={{ bg: "gray.50" }}
                    />
                    </FormControl>

                    <FormControl>
                    <FormLabel fontSize="sm" fontWeight="semibold">Email</FormLabel>
                    <Input
                        name="user_email"
                        type="email"
                        value={contactForm.user_email}
                        placeholder="your.email@example.com"
                        size="sm"
                        isReadOnly
                        bg="gray.50"
                        _focus={{ bg: "gray.50" }}
                    />
                    </FormControl>
                </SimpleGrid>

                <FormControl isRequired>
                    <FormLabel fontSize="sm" fontWeight="semibold">Subject</FormLabel>
                    <Input
                    name="subject"
                    value={contactForm.subject}
                    onChange={handleContactInputChange}
                    placeholder="Brief description of your inquiry"
                    size="sm"
                    />
                </FormControl>

                <FormControl isRequired>
                    <FormLabel fontSize="sm" fontWeight="semibold">Message</FormLabel>
                    <Textarea
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactInputChange}
                    placeholder="Please provide details about your inquiry..."
                    rows={6}
                    size="sm"
                    resize="vertical"
                    />
                </FormControl>
                </VStack>
            </ModalBody>

            <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onContactClose}>
                Cancel
                </Button>
                <Button
                type="submit"
                colorScheme="brand"
                leftIcon={<FaPaperPlane />}
                isLoading={isSubmittingContact}
                loadingText="Sending..."
                >
                Send Message
                </Button>
            </ModalFooter>
            </form>
        </ModalContent>
        </Modal>

      <Footer />
      {/* Purchase Confirmation Modal */}
    <Modal isOpen={isPurchaseConfirmOpen} onClose={onPurchaseConfirmClose} size="lg" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent>
        <ModalHeader borderBottom="1px" borderColor="gray.200">
          <VStack align="start" spacing={1}>
            <Text fontSize="lg" fontWeight="bold">Confirm Your Purchase</Text>
            <Text fontSize="sm" color="gray.500" fontWeight="normal">Please review and confirm</Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={6}>
          <VStack spacing={5} align="stretch">
            {/* Product Details */}
            <Box bg="gray.50" p={4} borderRadius="md" border="1px" borderColor="gray.200">
              <VStack spacing={3} align="start">
                <HStack justify="space-between" w="full">
                  <Text fontWeight="semibold" color="gray.700">Product:</Text>
                  <Text color="gray.800">{selectedPlan?.hours} Hour Credit – {selectedPlan?.hours} hours of simulation time</Text>
                </HStack>
                <HStack justify="space-between" w="full">
                  <Text fontWeight="semibold" color="gray.700">Price:</Text>
                  <Text color="gray.800">€{selectedPlan?.price}.00 <Text as="span" fontSize="sm" color="gray.500">+ VAT may apply</Text></Text>
                </HStack>
                <HStack justify="space-between" w="full">
                  <Text fontWeight="semibold" color="gray.700">Purchase Type:</Text>
                  <Text color="gray.800">One-time purchase</Text>
                </HStack>
              </VStack>
            </Box>

            {/* Credit Validity Notice */}
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                <Text as="span" fontWeight="semibold">Credit Validity:</Text> Simulation credits are valid for 12 months from the date of purchase.
              </Text>
            </Alert>
{/* Consent Checkboxes */}
<VStack spacing={4} align="stretch" pt={2}>
  <Checkbox
    isChecked={purchaseConsent.immediateProvision}
    onChange={(e) => setPurchaseConsent(prev => ({ ...prev, immediateProvision: e.target.checked }))}
    colorScheme="brand"
    size="md"
    alignItems="flex-start"
  >
    <Text fontSize="sm" color="gray.700" ml={1}>
      I expressly request the immediate provision of the digital service and acknowledge that I lose my right of withdrawal once the service has been used.
    </Text>
  </Checkbox>

  <Checkbox
    isChecked={purchaseConsent.termsAccepted}
    onChange={(e) => setPurchaseConsent(prev => ({ ...prev, termsAccepted: e.target.checked }))}
    colorScheme="brand"
    size="md"
    alignItems="flex-start"
  >
    <Text fontSize="sm" color="gray.700" ml={1}>
      I have read and accept the{' '}
      <Link as={RouterLink} to="/terms" color="brand.500" fontWeight="semibold" isExternal>
        Terms of Service
      </Link>{' '}
      and the{' '}
      <Link as={RouterLink} to="/privacy" color="brand.500" fontWeight="semibold" isExternal>
        Privacy Policy 
      </Link>
      {' '} last update: 05.01.2026.      
      <br />
      <Text as="span" fontSize="xs" color="gray.500" fontStyle="italic">
        Ich habe die AGB gelesen und akzeptiere sie sowie die Datenschutzerklärung.
      </Text>
    </Text>
  </Checkbox>
</VStack>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px" borderColor="gray.200" pt={4}>
          <Button variant="ghost" mr={3} onClick={onPurchaseConfirmClose}>
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            onClick={handleConfirmedPurchase}
            isDisabled={!purchaseConsent.immediateProvision || !purchaseConsent.termsAccepted}
            isLoading={isSubmittingContact}
            loadingText="Redirecting..."
            rightIcon={<Icon as={FaArrowLeft} transform="rotate(180deg)" />}
          >
            Jetzt bezahlen
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    </Box>
  );
};

export default BuyCredits;